export interface DisplayModelUsageEntry {
  model: string | null;
  provider: string | null;
  timestampMs?: number | null;
  totalTokens?: number | null;
}

export interface DisplayModelSummary {
  currentDisplayProvider: string | null;
  currentModelRef: string | null;
  currentModelTimestampMs: number | null;
  dominantDisplayProvider: string | null;
  dominantModelRef: string | null;
}

interface DominantModelCandidate {
  displayProvider: string | null;
  latestTimestampMs: number;
  modelRef: string | null;
  totalTokens: number;
}

export function normalizeDisplayModelRef(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  return trimmed || null;
}

export function displayProviderFromModelRef(
  modelRef: string | null,
): string | null {
  if (!modelRef) {
    return null;
  }

  const slashIndex = modelRef.indexOf("/");
  if (slashIndex <= 0) {
    return null;
  }

  const parts = modelRef.split("/");
  const head = parts[0]?.toLowerCase();
  if (head === "openrouter") {
    return displayProviderFromOpenRouterSubProvider(parts[1]);
  }
  if (head === "bedrock-anthropic") {
    return "anthropic";
  }

  return head ?? null;
}

function displayProviderFromOpenRouterSubProvider(
  value: string | undefined,
): string | null {
  const provider = normalizeDisplayProvider(value);
  if (!provider) {
    return "openrouter";
  }
  if (
    provider === "deepseek" ||
    provider === "z-ai" ||
    provider === "glm" ||
    provider === "minimax" ||
    provider === "x-ai" ||
    provider === "xai" ||
    provider === "xiaomi"
  ) {
    if (provider === "x-ai" || provider === "xai") {
      return "xai";
    }
    return provider === "z-ai" || provider === "glm" ? "zhipu" : provider;
  }
  return "openrouter";
}

export interface ModelIconPair {
  /** Model vendor whose brand mark is the main icon (e.g. "deepseek", "anthropic"). */
  vendor: string | null;
  /** Serving provider rendered as a small badge, or null when the model is
   *  served directly by its vendor (no badge). */
  servingProvider: "openrouter" | "aws" | "azure" | null;
  /** Hover label naming the full route, e.g. "DeepSeek via OpenRouter" or
   *  "Anthropic via AWS Bedrock (Anthropic Messages)". */
  transportLabel: string | null;
}

/** Split a model ref into (model vendor, serving provider) for two-icon display.
 *  Vendor drives the main icon; servingProvider drives the corner badge so
 *  e.g. DeepSeek-via-OpenRouter is distinguishable from a vendor-direct route. */
export function resolveModelIconPair(modelRef: string | null): ModelIconPair {
  const none: ModelIconPair = {
    servingProvider: null,
    transportLabel: null,
    vendor: null,
  };
  if (!modelRef) {
    return none;
  }
  const parts = modelRef.split("/");
  const head = parts[0]?.toLowerCase();
  switch (head) {
    case "openrouter": {
      const vendor =
        displayProviderFromOpenRouterSubProvider(parts[1]) ?? "openrouter";
      return {
        servingProvider: "openrouter",
        transportLabel: "via OpenRouter",
        vendor: vendor === "openrouter" ? null : vendor,
      };
    }
    case "azure": {
      const modelId = parts[1]?.toLowerCase() ?? "";
      return {
        servingProvider: "azure",
        transportLabel: "via Azure AI Foundry",
        vendor: vendorFromAzureModelId(modelId),
      };
    }
    case "bedrock": {
      return {
        servingProvider: "aws",
        transportLabel: "via AWS Bedrock (Converse)",
        vendor: "anthropic",
      };
    }
    case "bedrock-openai": {
      return {
        servingProvider: "aws",
        transportLabel: "via AWS Bedrock (mantle Responses)",
        vendor: "openai",
      };
    }
    case "bedrock-anthropic": {
      return {
        servingProvider: "aws",
        transportLabel: "via AWS Bedrock (Anthropic Messages)",
        vendor: "anthropic",
      };
    }
    case "vertex": {
      return {
        servingProvider: null,
        transportLabel: "via Google Vertex AI",
        vendor: "google",
      };
    }
    default: {
      // Vendor-direct route (openai/, anthropic/, google/, ...): no badge.
      return {
        servingProvider: null,
        transportLabel: null,
        vendor: head ?? null,
      };
    }
  }
}

function vendorFromAzureModelId(modelId: string): string | null {
  if (modelId.includes("kimi")) {
    return "moonshot";
  }
  if (modelId.includes("deepseek")) {
    return "deepseek";
  }
  return null;
}

export function normalizeDisplayProvider(value: unknown): string | null {
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim().toLowerCase();
  if (!trimmed || trimmed === "arti") {
    return null;
  }

  return trimmed;
}

export function resolveDisplayProvider(
  modelRef: string | null,
  rawProvider: string | null,
): string | null {
  return (
    displayProviderFromModelRef(modelRef) ??
    normalizeDisplayProvider(rawProvider)
  );
}

export function resolveDisplayModelSummary(
  entries: readonly DisplayModelUsageEntry[] | null | undefined,
): DisplayModelSummary {
  if (!entries?.length) {
    return {
      currentDisplayProvider: null,
      currentModelRef: null,
      currentModelTimestampMs: null,
      dominantDisplayProvider: null,
      dominantModelRef: null,
    };
  }

  let currentDisplayProvider: string | null = null;
  let currentModelRef: string | null = null;
  let currentModelTimestampMs: number | null = null;
  const dominantByKey = new Map<string, DominantModelCandidate>();

  for (const entry of entries) {
    const modelRef = normalizeDisplayModelRef(entry.model);
    const displayProvider = resolveDisplayProvider(
      modelRef,
      normalizeDisplayProvider(entry.provider),
    );
    const timestampMs =
      typeof entry.timestampMs === "number" &&
      Number.isFinite(entry.timestampMs)
        ? entry.timestampMs
        : -1;

    if (
      currentModelTimestampMs === null ||
      timestampMs > currentModelTimestampMs ||
      (timestampMs === currentModelTimestampMs &&
        currentModelRef === null &&
        modelRef !== null)
    ) {
      currentDisplayProvider = displayProvider;
      currentModelRef = modelRef;
      currentModelTimestampMs = timestampMs >= 0 ? timestampMs : null;
    }

    if (!displayProvider) {
      continue;
    }

    const key = modelRef === null ? `provider:${displayProvider}` : modelRef;
    const totalTokens =
      typeof entry.totalTokens === "number" &&
      Number.isFinite(entry.totalTokens)
        ? entry.totalTokens
        : 0;
    const previous = dominantByKey.get(key);
    if (!previous) {
      dominantByKey.set(key, {
        displayProvider,
        latestTimestampMs: timestampMs,
        modelRef,
        totalTokens,
      });
      continue;
    }

    dominantByKey.set(key, {
      displayProvider,
      latestTimestampMs: Math.max(previous.latestTimestampMs, timestampMs),
      modelRef,
      totalTokens: previous.totalTokens + totalTokens,
    });
  }

  let dominantModelRef: string | null = null;
  let dominantDisplayProvider: string | null = null;
  let bestTokens = -1;
  let bestTimestampMs = -1;

  for (const candidate of dominantByKey.values()) {
    if (
      candidate.totalTokens > bestTokens ||
      (candidate.totalTokens === bestTokens &&
        candidate.latestTimestampMs > bestTimestampMs)
    ) {
      dominantModelRef = candidate.modelRef;
      dominantDisplayProvider = candidate.displayProvider;
      bestTokens = candidate.totalTokens;
      bestTimestampMs = candidate.latestTimestampMs;
    }
  }

  return {
    currentDisplayProvider,
    currentModelRef,
    currentModelTimestampMs,
    dominantDisplayProvider,
    dominantModelRef,
  };
}
